import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageFromPath } from '../../lib/utils/geolocation';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
}

export function SEOHead({ title, description, keywords }: SEOHeadProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const currentLang = getLanguageFromPath(location.pathname) || 'en';

  useEffect(() => {
    // Update HTML lang attribute
    document.documentElement.lang = currentLang;

    // Update title
    const pageTitle = title || t('seo.title');
    document.title = pageTitle;

    // Update meta description
    const metaDescription = description || t('seo.description');
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute('content', metaDescription);
    }

    // Update meta keywords
    const metaKeywords = keywords || t('seo.keywords');
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (keywordsMeta) {
      keywordsMeta.setAttribute('content', metaKeywords);
    }

    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', pageTitle);
    }

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', metaDescription);
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      const currentUrl = currentLang === 'en' 
        ? `https://broilerfcr.com${location.pathname}`
        : `https://broilerfcr.com/tr${location.pathname === '/tr' ? '' : location.pathname.replace('/tr', '')}`;
      ogUrl.setAttribute('content', currentUrl);
    }

    // Update Twitter Card tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', pageTitle);
    }

    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', metaDescription);
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      const canonicalUrl = currentLang === 'en' 
        ? `https://broilerfcr.com${location.pathname}`
        : `https://broilerfcr.com/tr${location.pathname === '/tr' ? '' : location.pathname.replace('/tr', '')}`;
      canonical.setAttribute('href', canonicalUrl);
    }

    // Update hreflang links
    const updateHreflang = (hreflang: string, href: string) => {
      let link = document.querySelector(`link[hreflang="${hreflang}"]`);
      if (link) {
        link.setAttribute('href', href);
      }
    };

    const basePath = location.pathname === '/tr' ? '/' : location.pathname.replace('/tr', '');
    updateHreflang('en', `https://broilerfcr.com${basePath}`);
    updateHreflang('tr', `https://broilerfcr.com/tr${basePath === '/' ? '' : basePath}`);
    updateHreflang('x-default', `https://broilerfcr.com${basePath}`);

  }, [location.pathname, currentLang, title, description, keywords, t]);

  return null; // This component doesn't render anything
}